import React, { useState, useEffect } from 'react';
import './App.css';
import * as XLSX from 'xlsx';

// 1. We import from our new API file instead of supabaseClient
import { 
  fetchSurveys, 
  deleteSurvey, 
  fetchResponseCount, 
  fetchSurveyResponses 
} from './api';

function SurveyList({ goHome, onEdit }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQrFor, setShowQrFor] = useState(null);
  const [responseCounts, setResponseCounts] = useState({});

  useEffect(() => {
    loadSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSurveys = async () => {
    try {
      // 2. Use the API function to get surveys
      const data = await fetchSurveys();
      setSurveys(data || []);

      // 3. Fetch counts using the API function
      if (data && data.length > 0) {
        const counts = {};
        for (const survey of data) {
          // api.js handles the complex query, we just ask for the number
          counts[survey.id] = await fetchResponseCount(survey.id);
        }
        setResponseCounts(counts);
      }
    } catch (error) {
      console.error('Error loading surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this survey?')) return;
    
    try {
      // 4. Use API to delete
      await deleteSurvey(id);
      alert('Survey deleted');
      loadSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      alert('Error deleting survey');
    }
  };

  const exportToExcel = async (survey) => {
    try {
      // 5. Use API to get raw response data
      const responses = await fetchSurveyResponses(survey.id);

      if (!responses || responses.length === 0) {
        alert('No responses to export');
        return;
      }

      // Transform responses into Excel-friendly format
      const excelData = responses.map(response => {
        const row = {
          'Submission ID': response.id,
          'Submitted At': new Date(response.created_at).toLocaleString()
        };

        // Add each question and its answer
        if (survey.questions && Array.isArray(survey.questions)) {
          survey.questions.forEach(question => {
            const answer = response.answers?.[question.id];
            // Handle array answers (checkboxes)
            row[question.text || 'Untitled Question'] = Array.isArray(answer) 
              ? answer.join('; ') 
              : answer || '';
          });
        }

        return row;
      });

      // Create workbook and worksheet (XLSX logic stays here as it is UI/Format specific)
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Responses');

      // Auto-fit columns
      const maxWidth = 20;
      const colWidths = Object.keys(excelData[0] || {}).map(() => maxWidth);
      ws['!cols'] = colWidths.map(width => ({ wch: width }));

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${survey.title || 'Survey'}_responses_${timestamp}.xlsx`;

      // Download
      XLSX.writeFile(wb, filename);
      alert('Excel file downloaded successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Error creating Excel file');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;

  return (
    <div className="flex flex-col min-h-screen bg-white p-6">
      <div className="max-w-4xl mx-auto w-full">
        <h2 className="text-3xl font-bold mb-6 text-blue-900">My Surveys</h2>
        {surveys.length === 0 && <p className="text-gray-600">No surveys yet.</p>}
        {surveys.map(survey => (
          <div key={survey.id} className="border border-blue-200 p-6 rounded-lg mb-4 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-2xl font-bold text-blue-900 m-0">{survey.title || 'Untitled Survey'}</h3>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b border-blue-100">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Created</span>
                <span className="text-sm text-blue-900 mt-1">{survey.created_at ? new Date(survey.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Responses</span>
                <span className="text-sm font-semibold text-blue-700 mt-1">{responseCounts[survey.id] || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Questions</span>
                <span className="text-sm font-semibold text-blue-900 mt-1">{(survey.questions || []).length}</span>
              </div>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <button className="px-4 py-2 rounded-md font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md" onClick={() => onEdit && onEdit(survey)}>Edit</button>
              <button className="px-4 py-2 rounded-md font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md" onClick={() => { const link = `${window.location.origin}${window.location.pathname}?survey=${survey.id}`; navigator.clipboard?.writeText(link); setShowQrFor(survey.id === showQrFor ? null : survey.id); alert('Share link copied'); }}>Share</button>
              <button className="px-4 py-2 rounded-md font-medium text-sm transition-all bg-blue-100 text-blue-700 hover:bg-blue-200 shadow-sm hover:shadow-md" onClick={() => exportToExcel(survey)}>Export Excel</button>
              <button className="px-4 py-2 rounded-md font-medium text-sm transition-all bg-red-100 text-red-700 hover:bg-red-200 shadow-sm hover:shadow-md" onClick={() => handleDelete(survey.id)}>Delete</button>
            </div>
            {showQrFor === survey.id && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-semibold text-blue-900 mb-3">Scan QR or use link (copied to clipboard):</div>
                <img alt="qr" src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}${window.location.pathname}?survey=${survey.id}`)}`} className="border border-blue-300 p-2 bg-white rounded" />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 text-center">
        <button className="btn btn-primary" onClick={goHome}>Back to Home</button>
      </div>
    </div>
  );
}

export default SurveyList;